import React, {
  useEffect,
  useRef,
  useState
} from 'react';

import Footer from '../../layout/footer/footer';
import { httpClient } from '../../services/httpClient';

import './chartPage.css';


// =====================================================
// API
// =====================================================

export const get_chart = async () => {
  const response = await httpClient.get('/api/chart');

  return response?.data ?? [];
};


// =====================================================
// Person Modal
// =====================================================

const PersonModal = ({
  person,
  isOpen,
  onClose
}) => {

  if (!isOpen || !person) {
    return null;
  }


  return (
    <div
      className="modal-overlay"
      onClick={onClose}
    >

      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
      >

        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="بستن"
        >
          &times;
        </button>


        <div className="modal-body">

          {/* ===============================
              Image
          =============================== */}

          <div className="modal-image-section">

            {person.imageUrl ? (

              <img
                src={person.imageUrl}
                alt={person.personName || ''}
                className="modal-person-image"
              />

            ) : (

              <div className="modal-image-placeholder">
                {person.personName
                  ?.charAt(0)
                  ?.toUpperCase()}
              </div>

            )}

          </div>


          {/* ===============================
              Information
          =============================== */}

          <div className="modal-info-section">

            <h2 className="modal-person-name">
              {person.personName}
            </h2>


            {person.position && (

              <div className="modal-position-row">

                <b>سمت: </b>

                <span className="modal-person-position">
                  {person.position}
                </span>

              </div>

            )}


            {person.description && (

              <div className="modal-description-wrapper">

                <b>توضیحات:</b>

                <p className="modal-person-description">
                  {person.description}
                </p>

              </div>

            )}


            {person.bio && (

              <div className="modal-bio-wrapper">

                <b>بیوگرافی:</b>

                <div
                  className="modal-person-bio"
                  dangerouslySetInnerHTML={{
                    __html: person.bio
                  }}
                />

              </div>

            )}

          </div>

        </div>

      </div>

    </div>
  );
};


// =====================================================
// Chart Page
// =====================================================

const ChartPage = () => {

  const [content, setContent] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [selectedPerson, setSelectedPerson] =
    useState(null);

  const [isModalOpen, setIsModalOpen] =
    useState(false);


  // Flickity instances
  const carouselRefs = useRef([]);


  // =====================================================
  // Fetch
  // =====================================================

  useEffect(() => {

    fetchData();

  }, []);


  // =====================================================
  // Initialize Carousel
  // =====================================================

  useEffect(() => {

    if (
      !loading &&
      !error &&
      content
    ) {

      const timer = setTimeout(() => {

        initializeFlickitySliders();

      }, 200);


      return () => {
        clearTimeout(timer);
      };

    }

  }, [
    loading,
    error,
    content
  ]);


  // =====================================================
  // Fetch Data
  // =====================================================

  const fetchData = async () => {

    try {

      const data = await get_chart();

      setContent(data);

    } catch (err) {

      console.error(err);

      setError(true);

    } finally {

      setLoading(false);

    }

  };


  // =====================================================
  // Open Modal
  // =====================================================

  const handlePersonClick = (person) => {

    setSelectedPerson(person);

    setIsModalOpen(true);

    document.body.style.overflow = 'hidden';

  };


  // =====================================================
  // Close Modal
  // =====================================================

  const closeModal = () => {

    setIsModalOpen(false);

    setSelectedPerson(null);

    document.body.style.overflow = '';

  };


  // =====================================================
  // Initialize Flickity
  // =====================================================

  const initializeFlickitySliders = () => {

    const carousels =
      document.querySelectorAll(
        '.flickity-carousel'
      );


    carousels.forEach(
      (carousel, index) => {

        // -------------------------------------------
        // Destroy old instance
        // -------------------------------------------

        if (
          carouselRefs.current[index]
        ) {

          try {

            carouselRefs.current[index].destroy();

          } catch (e) {

            console.warn(
              'Flickity destroy error:',
              e
            );

          }

          carouselRefs.current[index] = null;

        }


        // -------------------------------------------
        // Check Flickity
        // -------------------------------------------

        if (
          !window.Flickity ||
          !carousel.children.length
        ) {

          return;

        }


        // -------------------------------------------
        // Original / Clone count
        // -------------------------------------------

        const originalCount =
          parseInt(
            carousel.dataset.originalCount || '0',
            10
          );


        const cloneCount =
          parseInt(
            carousel.dataset.cloneCount || '0',
            10
          );


        // -------------------------------------------
        // Create Flickity
        // -------------------------------------------

        const flkty =
          new window.Flickity(
            carousel,
            {

              /*
               * IMPORTANT
               *
               * wrapAround is OFF
               * because we are creating our own
               * clone items.
               */

              wrapAround: false,


              // RTL
              rightToLeft: true,


              // Position
              cellAlign: 'right',

              contain: false,


              // Navigation
              pageDots: true,

              prevNextButtons: true,


              // Drag
              draggable: true,

              groupCells: 1,


              // Animation
              selectedAttraction: 0.015,

              friction: 0.4,


              // Images
              imagesLoaded: true,

              lazyLoad: false,


              // Cell
              cellSelector:
                '.managers-cell',


              // Resize
              resize: true,

              adaptiveHeight: false,

              setGallerySize: true,


              // Initial
              initialIndex: 0,

              percentPosition: true,


              // Auto Play
              autoPlay: 5000,

              pauseAutoPlayOnHover: true

            }
          );


        carouselRefs.current[index] =
          flkty;


        // -------------------------------------------
        // Infinite Loop
        // -------------------------------------------

        flkty.on(
          'settle',
          () => {

            const selectedIndex =
              flkty.selectedIndex;


            /*
             * وقتی وارد Cloneها شدیم
             */

            if (
              selectedIndex >= originalCount
            ) {

              const realIndex =
                selectedIndex -
                originalCount;


              /*
               * کمی صبر می‌کنیم تا Clone
               * کامل نمایش داده شود.
               */

              setTimeout(() => {

                try {

                  flkty.select(
                    realIndex,
                    false,
                    true
                  );

                } catch (e) {

                  console.warn(
                    'Flickity loop error:',
                    e
                  );

                }

              }, 30);

            }

          }
        );


        // -------------------------------------------
        // Resize after images
        // -------------------------------------------

        setTimeout(() => {

          try {

            flkty.resize();

            flkty.reposition();

          } catch (e) {

            console.warn(
              'Flickity resize error:',
              e
            );

          }

        }, 500);


        // -------------------------------------------
        // Second resize
        // -------------------------------------------

        setTimeout(() => {

          try {

            flkty.resize();

            flkty.reposition();

          } catch (e) {

            console.warn(
              'Flickity second resize error:',
              e
            );

          }

        }, 1000);

      }
    );

  };


  // =====================================================
  // Image Error
  // =====================================================

  const handleImageError = (e) => {

    const image =
      e.currentTarget;


    if (!image) {
      return;
    }


    image.style.display = 'none';


    const parent =
      image.parentElement;


    if (
      parent &&
      !parent.querySelector(
        '.image-placeholder'
      )
    ) {

      const placeholder =
        document.createElement('div');


      placeholder.className =
        'fixed-images image-placeholder';


      placeholder.innerHTML = '?';


      parent.appendChild(
        placeholder
      );

    }

  };


  // =====================================================
  // Positions
  // =====================================================

  const positions =
    content?.positions ||
    content ||
    [];


  // =====================================================
  // Render
  // =====================================================

  return (
    <>

      <section
        className="company-introduction assistants-sec"
        style={{
          marginTop: '40px'
        }}
      >

        <div className="container">

          <h1 className="company-introduction__title">
          </h1>


          <div className="company-introduction__content-box">

            {/* ==========================================
                Loading
            ========================================== */}

            {loading && (

              <p className="company-introduction__status">
                در حال بارگذاری...
              </p>

            )}


            {/* ==========================================
                Error
            ========================================== */}

            {error && (

              <p className="company-introduction__status company-introduction__status--error">

                در دریافت اطلاعات خطایی رخ داده است.
                لطفاً مجدداً تلاش کنید.

              </p>

            )}


            {/* ==========================================
                Empty
            ========================================== */}

            {!loading &&
              !error &&
              positions.length === 0 && (

                <p className="company-introduction__status">

                  هیچ سمتی برای نمایش وجود ندارد.

                </p>

              )}


            {/* ==========================================
                Positions
            ========================================== */}

            {!loading &&
              !error &&
              positions.length > 0 && (

                positions.map(
                  (position, index) => {

                    // --------------------------------
                    // Persons
                    // --------------------------------

                    const persons =
                      position.chartPersons || [];


                    const personCount =
                      persons.length;


                    /*
                     * 5 نفر به بالا:
                     * Carousel
                     */

                    const useFlickity =
                      personCount >= 5;


                    /*
                     * تعداد Clone
                     *
                     * چون در موبایل 3 کارت نمایش می‌دهیم،
                     * سه آیتم اول را Clone می‌کنیم.
                     */

                    const cloneCount =
                      useFlickity
                        ? Math.min(
                            3,
                            personCount
                          )
                        : 0;


                    /*
                     * Original + Clone
                     */

                    const carouselPersons =
                      useFlickity
                        ? [
                            ...persons,
                            ...persons.slice(
                              0,
                              cloneCount
                            )
                          ]
                        : persons;


                    return (

                      <div
                        className="assistants-sec__managers"
                        key={
                          position.id ||
                          index
                        }
                      >

                        {/* ======================================
                            Position title
                        ====================================== */}

                        <div className="mimt__head">

                          <h4 className="mimt__head__title">
                            {position.title}
                          </h4>

                        </div>


                        {/* ======================================
                            Persons
                        ====================================== */}

                        {personCount > 0 ? (

                          <div
                            className={
                              `persons-container ${
                                useFlickity
                                  ? 'has-carousel'
                                  : 'has-grid'
                              }`
                            }
                          >

                            {/* ==================================
                                CAROUSEL
                            ================================== */}

                            {useFlickity ? (

                              <div
                                className="flickity-carousel managers-carousell"

                                data-carousel-index={
                                  index
                                }

                                data-original-count={
                                  personCount
                                }

                                data-clone-count={
                                  cloneCount
                                }
                              >

                                {carouselPersons.map(
                                  (
                                    person,
                                    personIndex
                                  ) => (

                                    <div
                                      key={
                                        `carousel-${index}-${personIndex}`
                                      }

                                      className="managers-cell"

                                      onClick={() =>
                                        handlePersonClick(
                                          person
                                        )
                                      }
                                    >

                                      <div className="assistants-item">

                                        {/* =========================
                                            Image
                                        ========================= */}

                                        <div className="assistants-item__pic">

                                          {person.imageUrl ? (

                                            <img
                                              src={
                                                person.imageUrl
                                              }

                                              alt={
                                                person.personName ||
                                                ''
                                              }

                                              className="fixed-images"

                                              onError={
                                                handleImageError
                                              }

                                              loading="lazy"
                                            />

                                          ) : (

                                            <div className="fixed-images image-placeholder">

                                              {person.personName
                                                ?.charAt(
                                                  0
                                                )
                                                ?.toUpperCase()}

                                            </div>

                                          )}

                                        </div>


                                        {/* =========================
                                            Content
                                        ========================= */}

                                        <div className="assistants-item__content">

                                          <h4>
                                            {
                                              person.personName
                                            }
                                          </h4>

                                          <span>
                                            {
                                              person.position
                                            }
                                          </span>

                                        </div>

                                      </div>

                                    </div>

                                  )
                                )}

                              </div>

                            ) : (

                              /* ==================================
                                  GRID
                              ================================== */

                              <div className="persons-grid">

                                {persons.map(
                                  (
                                    person,
                                    personIndex
                                  ) => (

                                    <div
                                      key={
                                        person.id ||
                                        personIndex
                                      }

                                      className="managers-cell"

                                      onClick={() =>
                                        handlePersonClick(
                                          person
                                        )
                                      }
                                    >

                                      <div className="assistants-item">

                                        <div className="assistants-item__pic">

                                          {person.imageUrl ? (

                                            <img
                                              src={
                                                person.imageUrl
                                              }

                                              alt={
                                                person.personName ||
                                                ''
                                              }

                                              className="fixed-images"

                                              onError={
                                                handleImageError
                                              }

                                              loading="lazy"
                                            />

                                          ) : (

                                            <div className="fixed-images image-placeholder">

                                              {person.personName
                                                ?.charAt(
                                                  0
                                                )
                                                ?.toUpperCase()}

                                            </div>

                                          )}

                                        </div>


                                        <div className="assistants-item__content">

                                          <h4>
                                            {
                                              person.personName
                                            }
                                          </h4>

                                          <span>
                                            {
                                              person.position
                                            }
                                          </span>

                                        </div>

                                      </div>

                                    </div>

                                  )
                                )}

                              </div>

                            )}

                          </div>

                        ) : (

                          <div className="no-persons">

                            هیچ فردی اختصاص داده نشده است

                          </div>

                        )}

                      </div>

                    );

                  }
                )

              )}

          </div>

        </div>

      </section>


      {/* ==============================================
          Footer
      ============================================== */}

      <Footer />


      {/* ==============================================
          Modal
      ============================================== */}

      <PersonModal
        person={selectedPerson}
        isOpen={isModalOpen}
        onClose={closeModal}
      />

    </>
  );
};


export default ChartPage;
